import { useConversation, type QueryHistoryItem } from '../../contexts/ConversationContext';

function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

interface QueryHistoryItemProps {
  item: QueryHistoryItem;
  onSelect: (query: string) => void;
  onToggleStar: (id: string) => void;
}

function QueryHistoryItemComponent({ item, onSelect, onToggleStar }: QueryHistoryItemProps) {
  return (
    <div
      className="group px-2 py-1.5 bg-white border border-grey-medium/20 hover:border-mint transition-colors cursor-pointer"
      onClick={() => onSelect(item.query)}
    >
      <div className="flex items-start gap-1.5">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleStar(item.id);
          }}
          className="mt-0.5 flex-shrink-0"
        >
          <svg
            className={`w-3 h-3 transition-colors ${
              item.starred ? 'text-mint fill-mint' : 'text-grey-medium hover:text-mint'
            }`}
            fill={item.starred ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
            />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-black font-light truncate">{item.query}</p>
          <p className="text-[10px] text-grey-medium mt-0.5">{formatRelativeTime(item.timestamp)}</p>
        </div>
      </div>
    </div>
  );
}

export default function QueryHistorySidebar() {
  const { queryHistory, submitQuery, toggleStarred, clearHistory } = useConversation();

  // Sort: starred first, then by timestamp
  const sortedHistory = [...queryHistory].sort((a, b) => {
    if (a.starred && !b.starred) return -1;
    if (!a.starred && b.starred) return 1;
    return b.timestamp - a.timestamp;
  });

  if (queryHistory.length === 0) {
    return (
      <div className="p-3">
        <p className="font-mono text-[10px] text-grey-medium uppercase tracking-wider mb-2">
          Query History
        </p>
        <p className="text-xs text-grey-medium font-light">
          Your recent queries will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="font-mono text-[10px] text-grey-medium uppercase tracking-wider">
          Query History
        </p>
        <button
          onClick={clearHistory}
          className="text-[10px] text-grey-medium hover:text-coral transition-colors"
        >
          Clear
        </button>
      </div>
      <div className="space-y-1">
        {sortedHistory.map((item) => (
          <QueryHistoryItemComponent
            key={item.id}
            item={item}
            onSelect={submitQuery}
            onToggleStar={toggleStarred}
          />
        ))}
      </div>
    </div>
  );
}
